import type { QueryClient } from "@tanstack/react-query";
import type { TeacherClassItem } from "@/lib/services/teacherClassesService";
import {
  filterAbsencesLast60Days,
  getAttendance,
  getAttendanceHistoryForClass,
  getRoster,
} from "@/lib/services/attendanceService";
import { QueryKeys } from "@/lib/types";
import {
  getScheduleDayNamesFromClass,
  resolveAttendanceSessionDate,
} from "@/lib/utils/attendance/attendanceSessionDate";
import type { GetClassResponse } from "@/openapi/generated/models";
import { ClassesApi } from "@/openapi/generated";
import { createAuthenticatedApi } from "@/lib/utils";
import { prefetchTeacherClasses } from "./prefetchTeacherClasses";

/** Server prefetch for teacher class attendance `/admin/dashboard/attendance/[classId]`. */
export async function prefetchTeacherAttendanceClass(
  queryClient: QueryClient,
  teacherId: number,
  classId: number,
  accessToken: string,
  urlDateParam?: string | null,
) {
  const service = createAuthenticatedApi(ClassesApi, accessToken);

  await Promise.allSettled([
    prefetchTeacherClasses(queryClient, teacherId, accessToken),
    queryClient.prefetchQuery({
      queryKey: [QueryKeys.Class, classId],
      queryFn: () => service.getClassById({ id: classId }),
      staleTime: 5 * 60 * 1000,
    }),
    queryClient.prefetchQuery({
      queryKey: [QueryKeys.Attendance, "roster", classId],
      queryFn: () => getRoster(accessToken, classId),
    }),
  ]);

  const classDetail = queryClient.getQueryData<GetClassResponse>([
    QueryKeys.Class,
    classId,
  ]);
  const teacherClasses =
    queryClient.getQueryData<TeacherClassItem[]>([
      QueryKeys.TeacherClasses,
      teacherId,
    ]) ?? [];
  const classInfo = teacherClasses.find((c) => c.classId === classId);
  const scheduleDayNames = getScheduleDayNamesFromClass(
    classDetail,
    classInfo?.day,
  );
  const sessionDate = resolveAttendanceSessionDate(
    scheduleDayNames,
    urlDateParam,
  );

  await Promise.allSettled([
    queryClient.prefetchQuery({
      queryKey: [QueryKeys.Attendance, classId, sessionDate, teacherId],
      queryFn: () =>
        getAttendance(accessToken, teacherId, classId, sessionDate),
    }),
    queryClient.prefetchQuery({
      queryKey: [QueryKeys.Attendance, "history", classId, teacherId],
      queryFn: async () => {
        const history = await getAttendanceHistoryForClass(
          accessToken,
          teacherId,
          classId,
        );
        return filterAbsencesLast60Days(history);
      },
    }),
  ]);

  return queryClient;
}
